import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import { DialogActions, DialogContent, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Spinner from '../common/spinner';
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import { cloneDeep, remove, sortBy } from 'lodash';
import { getProdOrdersUnInvoiced } from '../modules/orderdata';
import { getOrderDisplayNumber } from '../domain/order';
const DEFAULT_SELECTION = '-';

export default function DeliveredOrderSelectorDialog(props) {
    const dispatch = useDispatch();
    const open = props.open;
    const confirm = props.confirm;
    const cancel = props.cancel;
    const unInvoicedOrders = useSelector((state) => state.orderdata.unInvoicedOrders);
    const fetchingUnInvoiced = useSelector((state) => state.orderdata.isFetchingProdOrdersUnInvoiced);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { t } = useTranslation();

    const [selectedOrders, setSelectedOrders] = React.useState([]);
    const [filteredOrderList, setFilteredOrderList] = React.useState(null);

    useEffect(() => {
        if (open && !unInvoicedOrders && !fetchingUnInvoiced) {
            dispatch(getProdOrdersUnInvoiced());
        }
    }, [open, unInvoicedOrders, fetchingUnInvoiced, dispatch]);

    useEffect(() => {
        if (selectedOrders && unInvoicedOrders) {
            let newList = cloneDeep(unInvoicedOrders);
            if (selectedOrders.length > 0) {
                // filter selected ordernumber away
                remove(newList, (o) => selectedOrders.find((sel) => sel.orderNumber === o.orderNumber));
                // filter away orders with non-matching invoice info
                remove(newList, (o) => o.invoicingInfoBase64 !== selectedOrders[0].invoicingInfoBase64);
            }
            newList = sortBy(newList, ['orderNumber']);
            setFilteredOrderList(newList);
        }
    }, [selectedOrders, unInvoicedOrders]);

    useEffect(() => {
        if (!open && selectedOrders && selectedOrders.length) setSelectedOrders([]);
    }, [open, selectedOrders]);

    const onChange = (evt) => {
        const selected = filteredOrderList.find((o) => o.orderNumber === evt.target.value);
        if (!selected) throw new Error('error');

        setSelectedOrders([...selectedOrders, selected]);
    };

    const onRemoveFromSelected = (orderNumber) => {
        let newList = cloneDeep(selectedOrders);
        remove(newList, (o) => o.orderNumber === orderNumber);
        setSelectedOrders(newList);
    };

    if (open)
        return (
            <Dialog
                onClose={cancel}
                aria-labelledby="simple-dialog-title"
                open={open}
                fullWidth={true}
                maxWidth={'md'}
                fullScreen={fullScreen}>
                <DialogContent dividers>
                    {filteredOrderList && (
                        <Grid container>
                            <Grid item xs={12}>
                                {t('invoice.selectOrdersForInvoice')}
                            </Grid>
                            <Grid item xs={12} sx={{ pt: '1em', pb: '1em' }}>
                                {selectedOrders.map((o) => (
                                    <Grid container key={o.orderNumber}>
                                        <Grid item xs={11} alignContent="center">
                                            {getOrderDisplayNumber(o)} - {o.customer.name}
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                                data-testid={'selected-order-item-' + o.orderNumber}
                                                aria-label="delete"
                                                color="primary"
                                                onClick={() => {
                                                    onRemoveFromSelected(o.orderNumber);
                                                }}>
                                                <CancelIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>

                            <Grid item xs={12}>
                                <Select
                                    required
                                    data-testid="order-selector"
                                    id="orderSelector"
                                    name="orderSelector"
                                    value={'-'}
                                    onChange={onChange}>
                                    <MenuItem value={DEFAULT_SELECTION}>-Valitse-</MenuItem>
                                    {filteredOrderList.map((opt) => (
                                        <MenuItem key={opt.orderNumber} value={opt.orderNumber}>
                                            {getOrderDisplayNumber(opt)} - {opt.customer.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>
                    )}
                    {!filteredOrderList && (
                        <Grid container>
                            <Grid item xs={12}>
                                {t('general.retrievingInformation')}
                                <Spinner />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="primary"
                        name="btn-modal-confirm"
                        onClick={() => {
                            confirm(selectedOrders);
                        }}
                        disabled={!selectedOrders || !selectedOrders.length}>
                        {t('production.makeInvoice')}
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                        variant="contained"
                        sx={{ bgcolor: 'grey.300', color: 'text.primary', '&:hover': { bgcolor: 'grey.400' } }}
                        name="btn-modal-cancel"
                        onClick={cancel}>
                        {t('buttons.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    return null;
}

DeliveredOrderSelectorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    confirm: PropTypes.func.isRequired,
    cancel: PropTypes.func.isRequired,
};
